import { FormActionsMui } from '@app/components/form/FormActionsMui';
import { Page } from '@app/components/page/Page';
import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import { useStationsFinder } from '@app/core/stations/hooks/useStations';
import { useFragmentQuery } from '@app/modules/fragments/hooks/useFragment';
import { useFragmentMediaUrlQuery } from '@app/modules/fragments/hooks/useFragmentMediaUrlQuery';
import { useRenderedVideoUrl } from '@app/modules/fragments/screens/view/components/video-editions/useRenderedVideoUrl';
import { ConnectionProfileDisplay } from '@app/modules/social-export/components/ConnectionProfileDisplay';
import { SocialExportContext } from '@app/modules/social-export/components/dialogs/social-export-context';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { useSocialExportFactory } from '@app/modules/social-export/hooks/useSocialExportFactory';
import { socialExport, videoEditor, webApi } from '@mi-front/api-clients';
import { GetFragmentDto } from '@mi-front/core';
import { QueriesView } from '@mi-front/ui';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { MEDIA_SOURCE_FRAGMENT, SocialExportMediaSelector } from './SocialExportMediaSelector';
import { SocialExportVideoPreview } from './SocialExportVideoPreview';

const publishToPlatformTitleMessage = defineMessage({
  id: 'fragments.socialExport.publishToPlatform',
  defaultMessage: 'Publicar en {platformName}',
});

export function SocialExportUploadPage() {
  const { fragmentId, platform: platformParam } = useParams<{
    fragmentId: string;
    platform: string;
  }>();
  const platform = socialPlatforms.get(platformParam!.toUpperCase())!;
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const _finder = useStationsFinder();
  const { search } = useLocation();
  const projectIdFromUrl = new URLSearchParams(search).get('projectId') ?? undefined;

  const [selectedSource, setSelectedSource] = useState<string>(
    () => projectIdFromUrl ?? MEDIA_SOURCE_FRAGMENT,
  );

  const fragmentQuery = useFragmentQuery(fragmentId);
  const mediaUrlQuery = useFragmentMediaUrlQuery(fragmentId);
  const projectsListQuery = videoEditor.useProjectsList(
    { sourceType: 'FRAGMENT', sourceId: fragmentId },
    { query: { enabled: true } },
  );

  const useExport = useSocialExportFactory(platform);

  const settingsQuery =
    socialExport.useSettingsGetConnection<socialExport.SettingsGetConnection200>(
      fragmentQuery.data?.stationId ?? '',
      platform.id,
      {
        query: {
          enabled: !!fragmentQuery.data?.stationId,
        },
      },
    );

  const uploadMutation = webApi.useCreateFragmentSocialUploads({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: webApi.getFragmentSocialUploadsByFragmentIdQueryKey(fragmentId),
        });
        notifyInfo(
          $t({
            id: 'fragments.socialExport.publicationStarted',
            defaultMessage: 'Publicación iniciada',
          }),
        );
      },
      onError: (error) => {
        notifyError(
          $t({
            id: 'fragments.socialExport.startPublicationError',
            defaultMessage: 'Ocurrió un error al iniciar la publicación, contacte a soporte',
          }),
        );
        console.log(error);
      },
    },
  });

  const projects = projectsListQuery.data ?? [];
  const selectedProject =
    selectedSource !== MEDIA_SOURCE_FRAGMENT
      ? (projects.find((p) => p.id === selectedSource) ?? null)
      : null;
  const { url: renderedVideoUrl } = useRenderedVideoUrl(selectedProject as videoEditor.Project);
  const projectsWithRender = projects.filter(
    (p) => p.renderJob?.status === videoEditor.publicJobWithResultDtoStatusEnum.COMPLETED,
  );
  const showMediaSelector = projects.length > 0;

  useEffect(() => {
    if (projectIdFromUrl) {
      setSelectedSource(projectIdFromUrl);
    }
  }, [projectIdFromUrl]);

  const handleUpload = async (fragment: GetFragmentDto, data: webApi.CreateSocialUploadDto) => {
    try {
      const sourceType =
        selectedSource === MEDIA_SOURCE_FRAGMENT
          ? webApi.CreateSocialUploadDtoSourceTypeEnum.FRAGMENT
          : webApi.CreateSocialUploadDtoSourceTypeEnum.VIDEO_EDITOR_PROJECT;
      const sourceId = selectedSource === MEDIA_SOURCE_FRAGMENT ? fragment.id : selectedSource;

      await uploadMutation.mutateAsync({
        data: {
          sourceType,
          sourceId,
          platform: platform.id as webApi.CreateSocialUploadDtoPlatformEnumKey,
          data,
        },
      });
      pushTo(`/fragments/${fragment.id}/publish`);
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  const { PublishForm } = useExport();

  const renderScreen = (
    fragment: GetFragmentDto,
    fragmentMediaUrl: string,
    defaultSettings?: socialExport.SettingsGetConnection200,
  ) => {
    const effectiveMediaUrl =
      selectedSource === MEDIA_SOURCE_FRAGMENT ? (fragmentMediaUrl ?? null) : renderedVideoUrl;

    const canPublish =
      effectiveMediaUrl !== null &&
      (selectedSource === MEDIA_SOURCE_FRAGMENT ||
        projectsWithRender.some((p) => p.id === selectedSource));

    const title = $t(publishToPlatformTitleMessage, { platformName: platform.name });

    return (
      <SocialExportContext.Provider value={{ mediaUrl: effectiveMediaUrl ?? '' }}>
        <Page htmlTitle={title}>
          <Page.Header icon={platform.Icon()} title={title} />
          <Page.Content maxWidth="lg">
            {showMediaSelector && (
              <Box sx={{ mb: 4, mt: 2 }}>
                <SocialExportMediaSelector
                  value={selectedSource}
                  onChange={setSelectedSource}
                  projects={projects as videoEditor.Project[]}
                />
              </Box>
            )}
            {!canPublish && selectedSource !== MEDIA_SOURCE_FRAGMENT
              ? $t({
                  id: 'fragments.socialExport.mediaSelector.renderNotReady',
                  defaultMessage:
                    'El video de este proyecto aún no está generado. Generalo desde la pestaña de edición de video.',
                })
              : effectiveMediaUrl && (
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={7}>
                      <Box mb={2}>
                        <ConnectionProfileDisplay
                          stationId={fragment.stationId}
                          platform={platform}
                        />
                      </Box>
                      <PublishForm
                        fragment={fragment}
                        videoUrl={effectiveMediaUrl}
                        onUpload={(data) => handleUpload(fragment, data)}
                        defaultSettings={defaultSettings}
                        actions={
                          <FormActionsMui
                            separator
                            saveIcon={<UploadIcon />}
                            saveLabel={$t({
                              id: 'socialExport.form.publish',
                              defaultMessage: 'Publicar',
                            })}
                            cancel
                          />
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <SocialExportVideoPreview videoUrl={effectiveMediaUrl} />
                    </Grid>
                  </Grid>
                )}
          </Page.Content>
        </Page>
      </SocialExportContext.Provider>
    );
  };

  return (
    <QueriesView<
      [GetFragmentDto, string, socialExport.SettingsGetConnection200, videoEditor.ProjectsList200]
    >
      queries={[fragmentQuery, mediaUrlQuery, settingsQuery, projectsListQuery]}
    >
      {([fragment, mediaUrl, settings]) => <>{renderScreen(fragment, mediaUrl, settings)}</>}
    </QueriesView>
  );
}
